import { RecordsData } from "../modals/patients/import-patients-modal/import-patients-modal.interface";
import { AuditHistoryRecord, CampaignDetails, CampaignListSorting } from "../model/campaign.interface";
import { CommunicationChannel, CommunicationHistoryRecord, CommunicationDetail, SmsCommunicationDetail } from "../model/campaign.interface";
import { recordsDataInterface, UnsubscribeRecord } from "../model/unsubscribe.interface";

export const CAMPAIGN_FEATURE_KEY = 'campaign';

// ─── Phase 1: Campaign List State ────────────────────────────────────────────

export interface CampaignListState {
  // Standard campaigns
  standardCampaigns: any[];
  standardTotal: number;
  standardFilteredCount: number;
  standardPage: number;
  standardPageSize: number;
  standardSorting: CampaignListSorting;
  standardStatusFilter: string[];

  // Archived campaigns
  archivedCampaigns: any[];
  archivedTotal: number;
  archivedFilteredCount: number;
  archivedPage: number;
  archivedPageSize: number;
  archivedSorting: CampaignListSorting;
  archivedStatusFilter: string[];

  // Feature flags from API
  enableSecureDriveImport: boolean;

  // Shared UI state
  activeTab: number;
  search: string;
  loading: boolean;
  error: string | null;
}

// ─── Phase 2: Campaign Detail State ──────────────────────────────────────────

export interface CampaignDetailState {
  campaignId: string | null;
  campaignDetails: CampaignDetails | null;
  isEditMode: boolean;
  loading: boolean;
  saving: boolean;
  error: string | null;
  disableAuditListDownload: boolean;
}

// ─── Phase 3: Campaign Workflow State ────────────────────────────────────────

export interface CampaignWorkflowState {
  workflowTabData: any | null;
  communicationNodeData: Record<string, any> | null;
  activeCommunicationId: string | null;
  /** Incremented to signal vis-network label update; consumed by work-flow.component */
  nodeLabelUpdate: { stateId: string; label: string } | null;
  /** Incremented to signal vis-network fit; consumed by work-flow.component */
  fitNetworkTrigger: number;
}

// ─── Phase 2b: Campaign Audit History State ─────────────────────────────────

export interface CampaignAuditHistoryState {
  records: AuditHistoryRecord[];
  totalCount: number;
  pageNumber: number;
  limit: number;
  loading: boolean;
  error: string | null;
}

// ─── Communication History Search State ────────────────────────────────────

export interface CommunicationHistoryFilters {
  campaignName: string;
  listName: string;
}

export interface CommunicationHistoryState {
  records: CommunicationHistoryRecord[];
  selectedRecord: CommunicationHistoryRecord | null;
  channel: CommunicationChannel;
  contact: string;
  filters: CommunicationHistoryFilters;
  matchingRecordCount: number;
  hasMore: boolean;
  cursor: string | null;
  limit: number;
  sortOrder: 'ASC' | 'DESC';
  hasSearched: boolean;
  loading: boolean;
  error: string | null;
  communicationDetail: CommunicationDetail | SmsCommunicationDetail | null;
  detailLoading: boolean;
  detailError: string | null;
}

// ─── Phase 4: Import Patient State ───────────────────────────────────────────

export interface CampaignImportState {
  fileExistOnRedis: boolean;
  fileId: number | null;
  isEmailOrPhonePresent: boolean;
  isFileValidating: boolean;
  isDataMappingGridPopulated: boolean;
  recordsData: RecordsData;
  hasUnsavedChanges: boolean;
  fileUploadCompleted: boolean;
};

export interface CampaignUsubImportState {
  mode: 'manual' | 'import' | null;
  listType: UnsubscribeListType;
  selectedDomain: string | null;
  manualEmails: string[];
  manualPhones: string[];
  fileName: string | null;
  fileNameWithId: string | null;
  fileId: number | null;
  isEmailOrPhonePresent: boolean;
  isFileValidating: boolean;
  isDataMappingGridPopulated: boolean;
  hasUnsavedChanges: boolean;
  fileUploadCompleted: boolean;
  uploadProgress: number;
  heartbeatActive: boolean;
  lastValidationStatus: string | null;
  lastUploadStatus: string | null;
  recordsData: recordsDataInterface;
  actualValidRows: number | null;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
}


// ─── Phase 4: Unsubscribe Management State ──────────────────────────────────

export type UnsubscribeListType = 'email' | 'sms';

export interface UnsubscribeListSorting {
  sortOrder: 'ASC' | 'DESC';
  sortBy: string;
  columnName: string;
}

export interface CampaignUnsubscribeState {
  activeTab: UnsubscribeListType;

  emailRecords: UnsubscribeRecord[];
  emailTotal: number;
  emailPage: number;
  emailSorting: UnsubscribeListSorting;
  emailSearch: string;
  emailSelectedDomain: string | null;
  emailStartDate: Date | null;
  emailEndDate: Date | null;

  smsRecords: UnsubscribeRecord[];
  smsTotal: number;
  smsPage: number;
  smsSorting: UnsubscribeListSorting;
  smsSearch: string;
  smsStartDate: Date | null;
  smsEndDate: Date | null;

  domains: string[];
  domainsLoading: boolean;
  domainsError: string | null;

  activePhoneNumber: string | null;
  activePhoneLoading: boolean;
  activePhoneError: string | null;

  loading: boolean;
  error: string | null;
  exportDisabled: boolean;
  exportError: string | null;
  resubscribing: boolean;
  lastResubscribeType: UnsubscribeListType | null;
  lastSuccessMessage: string | null;
}

// ─── Root Campaign Feature State ─────────────────────────────────────────────

export interface CampaignState {
  list: CampaignListState;
  detail: CampaignDetailState;
  workflow: CampaignWorkflowState;
  auditHistory: CampaignAuditHistoryState;
  communicationHistory: CommunicationHistoryState;
  import: CampaignImportState;
  unsubscribe: CampaignUnsubscribeState;
  usubImport: CampaignUsubImportState;
}

// ─── Initial State Values ────────────────────────────────────────────────────

export const initialCampaignListState: CampaignListState = {
  standardCampaigns: [],
  standardTotal: 0,
  standardFilteredCount: 0,
  standardPage: 1,
  standardPageSize: 10,
  standardSorting: {
    sortOrder: 'DESC',
    sortBy: 'lastModifiedDateTimeUtc',
    columnName: '',
  },
  standardStatusFilter: [],

  archivedCampaigns: [],
  archivedTotal: 0,
  archivedFilteredCount: 0,
  archivedPage: 1,
  archivedPageSize: 10,
  archivedSorting: {
    sortOrder: 'DESC',
    sortBy: 'lastModifiedDateTimeUtc',
    columnName: '',
  },
  archivedStatusFilter: [],

  enableSecureDriveImport: false,

  activeTab: 0,
  search: '',
  loading: false,
  error: null,
};

export const initialCampaignDetailState: CampaignDetailState = {
  campaignId: null,
  campaignDetails: null,
  isEditMode: false,
  loading: false,
  saving: false,
  error: null,
  disableAuditListDownload: false
};

export const initialCampaignWorkflowState: CampaignWorkflowState = {
  workflowTabData: null,
  communicationNodeData: null,
  activeCommunicationId: null,
  nodeLabelUpdate: null,
  fitNetworkTrigger: 0,
};

export const initialCampaignImportState: CampaignImportState = {
  fileExistOnRedis: false,
  fileId: null,
  isEmailOrPhonePresent: false,
  isFileValidating: false,
  isDataMappingGridPopulated: false,
  recordsData: {
    totalRows: 0,
    invalidRows: 0,
    duplicateRows: 0,
    uniqueEmailCount: 0,
    uniquePhoneNumberCount: 0,
    totalFinalRecords: 0,
  },
  hasUnsavedChanges: false,
  fileUploadCompleted: false
};

export const initialCampaignUsubImportState: CampaignUsubImportState = {
  mode: null,
  listType: 'email',
  selectedDomain: null,
  manualEmails: [],
  manualPhones: [],
  fileName: null,
  fileNameWithId: null,
  fileId: null,
  isEmailOrPhonePresent: false,
  isFileValidating: false,
  isDataMappingGridPopulated: false,
  hasUnsavedChanges: false,
  fileUploadCompleted: false,
  uploadProgress: 0,
  heartbeatActive: false,
  lastValidationStatus: null,
  lastUploadStatus: null,
  recordsData: {
    totalRows: 0,
    invalidRows: 0,
    duplicateRows: 0,
    uniqueEmailCount: 0,
    uniquePhoneNumberCount: 0,
    totalFinalRecords: 0,
    actualValidRows: null,
  },
  actualValidRows: null,
  loading: false,
  error: null,
  successMessage: null,
};

export const initialCampaignUnsubscribeState: CampaignUnsubscribeState = {
  activeTab: 'email',

  emailRecords: [],
  emailTotal: 0,
  emailPage: 1,
  emailSorting: {
    sortOrder: 'DESC',
    sortBy: 'unsubscribeTime',
    columnName: 'unsubscribeTime',
  },
  emailSearch: '',
  emailSelectedDomain: null,
  emailStartDate: null,
  emailEndDate: null,

  smsRecords: [],
  smsTotal: 0,
  smsPage: 1,
  smsSorting: {
    sortOrder: 'DESC',
    sortBy: 'unsubscribeTime',
    columnName: 'unsubscribeTime',
  },
  smsSearch: '',
  smsStartDate: null,
  smsEndDate: null,

  domains: [],
  domainsLoading: false,
  domainsError: null,

  activePhoneNumber: null,
  activePhoneLoading: false,
  activePhoneError: null,

  loading: false,
  error: null,
  exportDisabled: false,
  exportError: null,
  resubscribing: false,
  lastResubscribeType: null,
  lastSuccessMessage: null,
};

export const initialCampaignAuditHistoryState: CampaignAuditHistoryState = {
  records: [],
  totalCount: 0,
  pageNumber: 1,
  limit: 10,
  loading: false,
  error: null,
};

export const initialCommunicationHistoryState: CommunicationHistoryState = {
  records: [],
  selectedRecord: null,
  channel: 'EMAIL',
  contact: '',
  filters: { campaignName: '', listName: '' },
  matchingRecordCount: 0,
  hasMore: false,
  cursor: null,
  limit: 10,
  sortOrder: 'DESC',
  hasSearched: false,
  loading: false,
  error: null,
  communicationDetail: null,
  detailLoading: false,
  detailError: null,
};

export const initialCampaignState: CampaignState = {
  list: initialCampaignListState,
  detail: initialCampaignDetailState,
  workflow: initialCampaignWorkflowState,
  auditHistory: initialCampaignAuditHistoryState,
  communicationHistory: initialCommunicationHistoryState,
  import: initialCampaignImportState,
  unsubscribe: initialCampaignUnsubscribeState,
  usubImport: initialCampaignUsubImportState,
};
